<template>
    <form
        :id="id"
        class="lni-c-isolated-workers-complaint lni-u-flex-auto lni-c-box--content"
    >
        <div class="lni-o-type-context--content">
            <h1 class="lni-u-heading--1">
                {{ text.formTitle }}
            </h1>
            <div class="lni-u-mt3 lni-u-reading-width--max">
                <h2 class="lni-u-heading--3">
                    {{ text.allegationsHeading }}
                </h2>
                <lni-checkbox-list
                    :id="`${id}_allegations_checklist`"
                    :labelText="text.allegationsChecklistLabel"
                    class="lni-u-mt2"
                    :required="true"
                    :changeAction="[`${id}/allegationsChange`, `${id}/syncStore`]"
                    :options="allegationsChecklistOptions"
                />
                <div role="status">
                    <transition name="lni-o-slide">
                        <div
                            v-if="showRetaliationMessages"
                            class="lni-c-informational-message --warn lni-u-pv1 lni-u-ph2"
                        >
                            {{ text.retaliationMessage }}
                        </div>
                    </transition>
                </div>
                <lni-input-textarea
                    :id="`${id}_additional-questions_describe-text`"
                    class="lni-u-mt3"
                    :fullWidth="true"
                    :labelText="text.additionalQuestionsDescribeTextLabel"
                    :required="true"
                    :filled="true"
                    :limit="2000"
                    :changeAction="`${id}/syncStore`"
                />
            </div>
            <div class="lni-u-mt3">
                <h2 class="lni-u-heading--3">
                    {{ text.complaintSubmissionHeading }}
                </h2>
                <div class="lni-u-mt2 lni-u-form-width--base">
                    <lni-select
                        :id="`${id}_complaint-submission_role`"
                        class="lni-u-full-width"
                        :labelText="text.complaintSubmissionRoleLabel"
                        :changeAction="[`${id}/syncStore`, `${id}/roleChange`]"
                        :options="complaintSubmissionRoleOptions"
                        :required="true"
                    >
                        <template #helperText>
                            <transition name="lni-o-slide">
                                <div v-if="showRetaliationMessages">
                                    {{ text.retaliationRoleMessage }}
                                </div>
                            </transition>
                        </template>
                    </lni-select>
                </div>
                <lni-collapse
                    :id="`${id}_complaint-submission_role_error_collapse`"
                    :visible="false"
                >
                    <lni-informational-message
                        :id="`${id}_complaint-submission_role_error`"
                        :message="text.complaintSubmissionRoleRetaliationNotice"
                        level="Error"
                        :large="false"
                    />
                </lni-collapse>
                <lni-collapse
                    :id="`${id}_complaint-submission_collapse`"
                    :visible="true"
                >
                    <div class="lni-u-full-width lni-u-flex lni-u-flex-wrap">
                        <lni-input-text
                            :id="`${id}_complaint-submission_first-name`"
                            class="lni-u-mr1 lni-u-mb1"
                            :labelText="text.complaintSubmissionFirstNameLabel"
                            :required="true"
                            :changeAction="`${id}/syncStore`"
                        />
                        <lni-input-text
                            :id="`${id}_complaint-submission_last-name`"
                            class="lni-u-mb1"
                            :labelText="text.complaintSubmissionLastNameLabel"
                            :required="true"
                            :changeAction="`${id}/syncStore`"
                        />
                    </div>
                    <div class="lni-u-full-width lni-u-flex lni-u-flex-wrap">
                        <lni-input-phone
                            :id="`${id}_complaint-submission_phone`"
                            class="lni-u-mr1 lni-u-mt1"
                            :isFieldset="false"
                            :filled="true"
                            :labelText="text.complaintSubmissionPhoneLabel"
                            :hasExtension="false"
                            :required="true"
                            :changeAction="`${id}/syncStore`"
                        />
                        <lni-input-email
                            :id="`${id}_complaint-submission_email`"
                            class="lni-u-mt0 lni-u-mb2"
                            :labelText="text.complaintSubmissionEmailLabel"
                            :filled="true"
                            :required="true"
                            :minlength="10"
                            :maxlength="50"
                            :changeAction="`${id}/syncStore`"
                        />
                    </div>
                    <div class="lni-u-form-width--base">
                        <lni-select
                            :id="`${id}_complaint-submission_language`"
                            class="lni-u-mt0 lni-u-full-width"
                            :labelText="text.complaintSubmissionLangLabel"
                            :changeAction="`${id}/syncStore`"
                            :options="complaintSubmissionLangOptions"
                            :required="true"
                        />
                    </div>
                    <lni-input-address
                        :id="`${id}_complaint-submission_address`"
                        class="lni-u-mt3"
                        :filled="true"
                        :labelText="text.complaintSubmissionAddressLabel"
                        :required="true"
                        :countyOptions="counties"
                        state="WA"
                        :changeAction="[`${id}/addressChange`, `${id}/syncStore`]"
                    />
                </lni-collapse>
            </div>
            <div>
                <h2 class="lni-u-heading--3">
                    {{ text.employerHeading }}
                </h2>
                <div class="lni-u-mt2 lni-u-form-width--base">
                    <lni-input-text
                        :id="`${id}_employer_name`"
                        class="lni-u-mb1"
                        :labelText="text.employerNameLabel"
                        :filled="true"
                        :required="true"
                        :changeAction="`${id}/syncStore`"
                        :fullWidth="true"
                    />
                </div>
                <div class="lni-u-full-width lni-u-flex lni-u-flex-wrap">
                    <lni-input-phone
                        :id="`${id}_employer_phone`"
                        class="lni-u-mr1 lni-u-mt1"
                        :isFieldset="false"
                        :filled="true"
                        :labelText="text.employerPhoneLabel"
                        :hasExtension="false"
                        :required="true"
                        :changeAction="`${id}/syncStore`"
                    />
                    <lni-input-email
                        :id="`${id}_employer_email`"
                        class="lni-u-mt0 lni-u-mb2"
                        :labelText="text.employerEmailLabel"
                        :filled="true"
                        :minlength="10"
                        :maxlength="50"
                        :changeAction="`${id}/syncStore`"
                    />
                </div>
                <lni-input-address
                    :id="`${id}_employer_address`"
                    class="lni-u-mt2"
                    :filled="true"
                    :labelText="text.employerAddressLabel"
                    :required="true"
                    :countyOptions="counties"
                    state="WA"
                    :changeAction="[`${id}/syncStore`, `${id}/addressChange`]"
                />
                <fieldset>
                    <legend class="lni-u-heading--4">
                        {{ text.contactHeading }}
                    </legend>
                    <div class="lni-u-full-width lni-u-flex lni-u-flex-wrap">
                        <lni-input-text
                            :id="`${id}_employer_business-contact-first-name`"
                            class="lni-u-mb1 lni-u-mr1"
                            :labelText="text.contactFirstNameLabel"
                            :filled="true"
                            :changeAction="`${id}/syncStore`"
                        />
                        <lni-input-text
                            :id="`${id}_employer_business-contact-last-name`"
                            class="lni-u-mb1"
                            :labelText="text.contactLastNameLabel"
                            :filled="true"
                            :changeAction="`${id}/syncStore`"
                        />
                    </div>
                    <div class="lni-u-full-width lni-u-flex lni-u-flex-wrap">
                        <lni-input-phone
                            :id="`${id}_employer_business-contact-phone`"
                            class="lni-u-mr1 lni-u-mt1"
                            :isFieldset="false"
                            :filled="true"
                            :labelText="text.contactPhoneLabel"
                            :hasExtension="false"
                            :changeAction="`${id}/syncStore`"
                        />
                        <lni-input-email
                            :id="`${id}_employer_business-contact-email`"
                            class="lni-u-mb2"
                            :labelText="text.contactEmailLabel"
                            :filled="true"
                            :minlength="10"
                            :maxlength="50"
                            :changeAction="`${id}/syncStore`"
                        />
                    </div>
                </fieldset>
            </div>
            <div class="lni-u-flex-column lni-u-mt2">
                <h2 class="lni-u-heading--3">
                    {{ text.additionalQuestionsHeading }}
                </h2>
                <div class="lni-u-flex lni-u-flex-column">
                    <lni-input-date
                        :id="`${id}_additional-questions_date-start-work`"
                        class="lni-u-mt2"
                        :labelText="text.additionalQuestionsDateStartWorkLabel"
                        customWidth="10"
                        :dense="true"
                        :filled="true"
                        :changeAction="[`${id}/syncStore`, `${id}/workDateChange`]"
                        :customValidityAction="`${id}/workDateValidation`"
                    />
                    <lni-input-date
                        :id="`${id}_additional-questions_date-end-work`"
                        :labelText="text.additionalQuestionsDateEndWorkLabel"
                        :helperText="text.additionalQuestionsDateEndWorkHelperText"
                        customWidth="10"
                        :dense="true"
                        :filled="true"
                        class="lni-u-mt2"
                        :changeAction="[`${id}/syncStore`, `${id}/workDateChange`]"
                    />
                    <fieldset class="lni-u-flex lni-u-flex-wrap lni-u-full-width lni-u-mt3">
                        <legend class="lni-u-heading--4">
                            {{ text.additionalQuestionsDateRangeComplaintLabel }}
                        </legend>
                        <lni-input-date
                            :id="`${id}_additional-questions_date-start-complaint`"
                            class="lni-u-mb1 lni-u-mr2"
                            :labelText="text.additionalQuestionsDateStartComplaintLabel"
                            customWidth="10"
                            :dense="true"
                            :filled="true"
                            :changeAction="[`${id}/syncStore`, `${id}/complaintDateChange`]"
                            :customValidityAction="`${id}/complaintDateValidation`"
                        />
                        <lni-input-date
                            :id="`${id}_additional-questions_date-end-complaint`"
                            :labelText="text.additionalQuestionsDateEndComplaintLabel"
                            customWidth="10"
                            :dense="true"
                            :filled="true"
                            class="lni-u-mb1"
                            :changeAction="[`${id}/syncStore`, `${id}/complaintDateChange`]"
                        />
                    </fieldset>
                </div>
            </div>
            <div class="lni-u-flex-column lni-u-mt4">
                <h2 class="lni-u-heading--3">
                    {{ text.docUploadHeading }}
                </h2>
                <div class="lni-u-mt2 lni-u-form-width--base">
                    <lni-select
                        :id="`${id}_upload_document-type`"
                        class="lni-u-full-width"
                        labelText="Select document type"
                        :options="docUploadTypes"
                    />
                </div>
                <div class="lni-u-mt2 lni-u-reading-width--max">
                    <lni-file-upload-form
                        :id="`${id}_upload`"
                        title=""
                        :hasVideos="true"
                        :sizeLimit="20971520"
                        :fileLimit="10"
                        :multiple="false"
                        :charLimit="200"
                        :fileTypes="docUploadFileTypes"
                        :uploadAction="`${id}/upload`"
                    >
                        <template slot="uploads">
                            <lni-file-uploads
                                :id="`${id}_file-uploads`"
                                :allowDelete="true"
                                @deleted="onDeleted"
                            />
                        </template>
                    </lni-file-upload-form>
                </div>
            </div>
            <div class="lni-u-mt3 lni-u-pt3 lni-u-bt lni-u-b--subtle">
                <lni-button
                    :id="`${id}_submit_button`"
                    :text="text.complaintSubmissionSubmitButtonLabel"
                    :errorContainerId="`${id}_errors`"
                    :clickAction="[`${id}/syncStore`, 'validateForm', `${id}/submit`]"
                />
                <div class="lni-u-mt3 lni-u-reading-width--max">
                    <lni-error-container
                        :id="`${id}_errors`"
                        :errorsCollectedFrom="trackedErrors"
                    />
                    <lni-informational-messages
                        :id="`${id}_post-submission-errors`"
                    />
                    <!-- TODO -->
                    <div v-if="displayFormError">
                        <span class="lni-u-heading--3">
                            {{ text.complaintSubmissionSubmitErrorHeading }}
                        </span>
                        <p>
                            {{ text.complaintSubmissionSubmitErrorText }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
export default {
    name: 'LniIsolatedWorkersComplaintForm',
    computed: {
        allegationsChecklistOptions() {
            return this.$store.state.appState?.allegationsChecklistOptions;
        },
        complaintSubmissionLangOptions() {
            return this.$store.state.appState?.complaintSubmissionLangOptions;
        },
        complaintSubmissionRoleOptions() {
            return this.$store.state.appState?.complaintSubmissionRoleOptions;
        },
        counties() {
            return this.$store.state.appState?.counties;
        },
        docUploadFileTypes() {
            return this.$store.state.appState?.docUploadFileTypes || [];
        },
        docUploadTypes() {
            return this.$store.state.appState?.docUploadTypes || [];
        },
        showRetaliationMessages() {
            return this.$store.getters[`${this.id}/retaliationSelected`];
        },
        text() {
            return this.$store.state.appState?.text;
        },
    },
    mounted() {
        this.addCustomValidityTests();
        this.$nextTick(() => {
            this.$store.commit(`${this.id}/ADD_TRACKED_ERRORS`, [
                `${this.id}_allegations_checklist`,
                `${this.id}_additional-questions_describe-text`,
                `${this.id}_complaint-submission_role`,
                `${this.id}_complaint-submission_first-name`,
                `${this.id}_complaint-submission_last-name`,
                `${this.id}_complaint-submission_phone`,
                `${this.id}_complaint-submission_email`,
                `${this.id}_complaint-submission_language`,
                `${this.id}_complaint-submission_address`,
                `${this.id}_employer_name`,
                `${this.id}_employer_phone`,
                `${this.id}_employer_address`,
                `${this.id}_additional-questions_date-start-work`,
                `${this.id}_additional-questions_date-end-work`,
                `${this.id}_additional-questions_date-start-complaint`,
                `${this.id}_additional-questions_date-end-complaint`,
                `${this.id}_upload`,
            ]);
            this.$store.commit(`setAttribute`, {
                id: `${this.id}_errors`,
                attribute: 'errorsCollectedFrom',
                value: this.trackedErrors,
            });
        });
    },
    methods: {
        addCustomValidityTests() {
            const workStartId = `${this.id}_additional-questions_date-start-work`;
            const workStartLabel = this.$store.state[workStartId].labelText;

            const workEndId = `${this.id}_additional-questions_date-end-work`;
            const workEndLabel = this.$store.state[workEndId].labelText;

            this.$store.commit(`${workStartId}/addValidityTest`, {
                validityTest: 'invalidDuration',
                global: state => `"${state.labelText}" cannot be after "${workEndLabel}".`,
                inline: () => 'Start date cannot be after end date.',
            }, {
                root: true,
            });

            this.$store.commit(`${workEndId}/addValidityTest`, {
                validityTest: 'invalidDuration',
                global: state => `"${state.labelText}" cannot be before "${workStartLabel}.`,
                inline: () => 'End date cannot be before start date.',
            }, {
                root: true,
            });

            const complaintStartId = `${this.id}_additional-questions_date-start-complaint`;
            const complaintStartLabel = this.$store.state[complaintStartId].labelText;

            const complaintEndId = `${this.id}_additional-questions_date-end-complaint`;
            const complaintEndLabel = this.$store.state[complaintEndId].labelText;

            this.$store.commit(`${complaintStartId}/addValidityTest`, {
                validityTest: 'invalidDuration',
                global: state => `"${state.labelText}" cannot be after "${complaintEndLabel}".`,
                inline: () => 'Start date cannot be after end date.',
            }, {
                root: true,
            });

            this.$store.commit(`${complaintEndId}/addValidityTest`, {
                validityTest: 'invalidDuration',
                global: state => `"${state.labelText}" cannot be before "${complaintStartLabel}".`,
                inline: () => 'End date cannot be before start date.',
            }, {
                root: true,
            });
        },
        onDeleted(files) {
            this.$store.commit('setAttribute', {
                id: `${this.id}_upload`,
                attribute: 'uploadedFiles',
                value: files,
            }, {
                root: true,
            });

            this.$store.commit('appState/SET_VALUE', {
                key:'documents',
                value: files,
            }, {
                root: true,
            });
        },
    }
};
</script>